import axios from "./AxiosClient";
import { Constants } from "@socialcoach/services";
import { scCatchServiceException } from "@/annotations";
import { Fan, FanActivity, FanSourceSystem, Page } from "./dtos";

const FAN_SERVICE = `${Constants.API_URL}/bedrock/fans`;

export interface FanRequest {
  playerId?: number;
  displayName?: string;
  email?: string;
  phoneNumber?: string;
  stateCode?: string;
  defaultLanguage?: string;
  internalIdentifier?: string;
  notes?: string;
  fanType?: "customer" | "partner";
  sourceSystem?: string;
  sourceCategory?: string;
  active?: boolean;
}

export type ExtraFanField = "latest_activity";
export interface FanFilterParams {
  search?: string;
  active?: boolean;
  extraFields?: ExtraFanField[];
  sourceSystem?: FanSourceSystem;
  playerId?: number;
  page?: number;
  perPage?: number;
}

export interface FanActivityFilterParams {
  activityType?: string;
  page?: number;
  perPage?: number;
}

export class FanServices {
  @scCatchServiceException()
  static async getFans(params?: FanFilterParams): Promise<Page<Fan>> {
    const { data } = await axios.get<Page<Fan>>(FAN_SERVICE, { params });
    return data;
  }

  @scCatchServiceException()
  static async getFan(id: string | number): Promise<Fan> {
    const { data } = await axios.get<Fan>(`${FAN_SERVICE}/${id}`);
    return data;
  }

  @scCatchServiceException()
  static async getFanActivities(id: string | number, params?: FanActivityFilterParams): Promise<Page<FanActivity>> {
    const { data } = await axios.get<Page<FanActivity>>(`${FAN_SERVICE}/${id}/activities`, {
      params,
    });
    return data;
  }

  @scCatchServiceException()
  static async createFan(request: FanRequest): Promise<Fan> {
    const { data } = await axios.post<Fan>(FAN_SERVICE, request);
    return data;
  }

  @scCatchServiceException()
  static async updateFan(id: string | number, request: FanRequest): Promise<Fan> {
    const { data } = await axios.patch<Fan>(`${FAN_SERVICE}/${id}`, request);
    return data;
  }

  @scCatchServiceException()
  static async mergeFan(id: number, duplicateFanId: number): Promise<Fan> {
    const { data } = await axios.post<Fan>(`${FAN_SERVICE}/${id}/merge`, { duplicateFanId });
    return data;
  }

  @scCatchServiceException()
  static async deleteFan(id: string | number): Promise<void> {
    await axios.delete(`${FAN_SERVICE}/${id}`);
  }
}
